import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProductById } from '../api/products';
import Header from '../components/Header';
import { addToCart, selectCartItems } from '../store/slice/cartSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Footer from '../components/Footer';

interface Product {
    category: string;
    description: string;
    id: number;
    image: string;
    price: number | any;
    rating: {
        count: number;
        rate: number | any;
    };
    title: string;
}

const ProductDescription = () => {
    const { productId }: any = useParams();

    const [quantity, setQuantity] = useState(1);
    const [product, setProduct] = useState<Product | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const cartItems = useAppSelector(selectCartItems);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const fetchProductsById = async () => {
        try {
            const response = await getProductById(productId);
            if (response) {
                setLoading(false);
                setProduct(response);
            } else {
                console.error("Failed to get product");
                setLoading(false);
            }
        } catch (error) {
            console.error("Failed to get product", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (productId) {
            fetchProductsById();
            const productInCart = cartItems.find((item: any) => item.id === parseInt(productId, 10));
            if (productInCart) {
                setQuantity(productInCart.quantity);
            }
        }
    }, [cartItems, productId]);

    const renderStars = (rating: any) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <span key={i} className={`text-yellow-400 ${i < rating ? '' : 'text-gray-300'}`}>
                    ★
                </span>
            );
        }
        return stars;
    };

    const handleIncrease = () => setQuantity((prev) => prev + 1);
    const handleDecrease = () => setQuantity((prev) => (prev > 1 ? prev - 1 : prev));

    const handleAddToCart = () => {
        const existingItem = cartItems.find((item: any) => item.id === parseInt(productId, 10));

        const finalQuantity = existingItem ? quantity : quantity;

        if (product) {
            dispatch(addToCart({ ...product, quantity: finalQuantity }));
            navigate('/cart');
        }
    };


    return (
        <div>
            <Header searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <div className="min-h-screen bg-white flex flex-col items-center p-4">
                <button
                    onClick={() => navigate(-1)}
                    className="text-gray-600 mb-4 self-start hover:underline"
                >
                    &larr; Back
                </button>

                {loading ? (
                    <div className="flex items-center justify-center h-screen">
                        <div role="status">
                            <svg
                                aria-hidden="true"
                                className="w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentFill"
                                />
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (

                    <div className=" max-w-4xl w-full p-6 ">
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex-1 flex justify-center items-center rounded-lg p-4">
                                <img
                                    src={product?.image}
                                    alt={product?.title}
                                    className="w-full max-w-sm object-contain"
                                />
                            </div>

                            <div className="flex-1 flex flex-col">
                                <h1 className="text-2xl font-bold text-gray-800 mb-2">{product?.title}</h1>
                                <div className="flex items-center mb-2">
                                    <span className="text-gray-700 mr-2">Rating:</span>
                                    {renderStars(Math.round(product?.rating?.rate))}
                                    <span className="text-gray-500 text-sm ml-2">
                                        ({product?.rating?.count} reviews)
                                    </span>
                                </div>
                                <p className="text-xl font-semibold text-gray-900 mb-4">${product?.price}</p>
                                <p className="text-gray-700 text-sm leading-relaxed mb-6">
                                    {product?.description}
                                </p>
                                <hr className="mb-6" />

                                <div className="flex items-center gap-4">
                                    <div className="flex items-center border rounded-lg overflow-hidden">
                                        <button
                                            className="px-4 py-2 bg-blushWhite hover:bg-lightcolor"
                                            onClick={handleDecrease}
                                        >
                                            -
                                        </button>
                                        <span className="px-6 py-2 text-gray-700">{quantity}</span>
                                        <button
                                            className="px-4 py-2 bg-blushWhite hover:bg-lightcolor"
                                            onClick={handleIncrease}
                                        >
                                            +
                                        </button>
                                    </div>
                                    <button
                                        className="flex-1 bg-ashBrown text-white py-2 px-4 rounded-lg hover:bg-lightCopper"
                                        onClick={handleAddToCart}
                                    >
                                        Add to Cart
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                )}

            </div>
            <Footer />
        </div>
    );
};

export default ProductDescription;
